export type StaffRole = 'ADMIN' | 'MODERATOR' | 'ACCOUNTANT' | 'SUPPORT';

export type StaffView =
  | 'LISTINGS_MODERATION'
  | 'APPEALS'
  | 'REVIEWS_MODERATION'
  | 'CONTENT_REPORTS'
  | 'GUIDES'
  | 'RESIDENT_VOICES'
  | 'COMMUTE_CHECK'
  | 'ORDERS'
  | 'SALES_INVOICES'
  | 'LEDGER'
  | 'USERS'
  | 'MAILBOX'
  | 'EMAIL_TEMPLATES'
  | 'ANALYTICS'
  | 'PRICING'
  | 'DISCOUNT_CODES'
  | 'LEGAL_DOCUMENTS';

/** Widoki obsługi i rola, która ma je domyślnie (ta sama lista co w serwerze: StaffView). */
export const STAFF_VIEWS: { id: StaffView; label: string; role: StaffRole }[] = [
  { id: 'LISTINGS_MODERATION', label: 'Moderacja i zgłoszenia ogłoszeń', role: 'MODERATOR' },
  { id: 'APPEALS', label: 'Odwołania i raport DSA', role: 'MODERATOR' },
  { id: 'REVIEWS_MODERATION', label: 'Opinie — moderacja', role: 'MODERATOR' },
  { id: 'CONTENT_REPORTS', label: 'Zgłoszenia treści', role: 'MODERATOR' },
  { id: 'GUIDES', label: 'Przewodniki po dzielnicach', role: 'MODERATOR' },
  { id: 'RESIDENT_VOICES', label: 'Głos mieszkańców', role: 'MODERATOR' },
  { id: 'COMMUTE_CHECK', label: 'Kontrola czasów dojazdu', role: 'MODERATOR' },
  { id: 'ORDERS', label: 'Zamówienia i zwroty', role: 'ACCOUNTANT' },
  { id: 'SALES_INVOICES', label: 'Faktury sprzedaży', role: 'ACCOUNTANT' },
  { id: 'LEDGER', label: 'Księgowość', role: 'ACCOUNTANT' },
  { id: 'USERS', label: 'Konta i użytkownicy', role: 'SUPPORT' },
  { id: 'MAILBOX', label: 'Skrzynka e-maili i SMS-ów', role: 'SUPPORT' },
  { id: 'EMAIL_TEMPLATES', label: 'Szablony e-maili', role: 'ADMIN' },
  { id: 'ANALYTICS', label: 'Statystyki portalu', role: 'ADMIN' },
  { id: 'PRICING', label: 'Cennik i promocje', role: 'ADMIN' },
  { id: 'DISCOUNT_CODES', label: 'Kody rabatowe', role: 'ADMIN' },
  { id: 'LEGAL_DOCUMENTS', label: 'Dokumenty prawne', role: 'ADMIN' },
];

export interface AccountSummary {
  id: string;
  kind: 'PERSONAL' | 'COMPANY';
  name: string;
  nip?: string;
}

export interface Profile {
  userId: string;
  email: string;
  name: string;
  account: AccountSummary;
  accountRole: 'ACCOUNT_OWNER' | 'AGENT';
  staffRoles: StaffRole[];
  /** Widoki obsługi dodane osobie poza jej rolami. */
  staffViews?: StaffView[];
  /** Konta, do których użytkownik może przejść (np. prywatne i firma, w której jest agentem). */
  accounts: AccountChoice[];
}

export interface AccountChoice {
  id: string;
  kind: 'PERSONAL' | 'COMPANY';
  name: string;
  role: 'ACCOUNT_OWNER' | 'AGENT';
}

export interface SessionResponse {
  accessToken: string;
  expiresAt: string;
  profile: Profile;
}

export type SessionState =
  { status: 'loading' } | { status: 'anonymous'; reason?: string } | { status: 'authenticated'; profile: Profile };

/** Rola obsługi portalu (administrator ma każdą). */
export function hasStaffRole(profile: Profile, role: StaffRole): boolean {
  return profile.staffRoles.includes('ADMIN') || profile.staffRoles.includes(role);
}

/** Widok obsługi: administrator, rola z tym widokiem albo widok dodany osobie. */
export function hasStaffView(profile: Profile, view: StaffView): boolean {
  const role = STAFF_VIEWS.find((v) => v.id === view)?.role;
  return (role !== undefined && hasStaffRole(profile, role)) || (profile.staffViews ?? []).includes(view);
}

export function isStaff(profile: Profile): boolean {
  return profile.staffRoles.length > 0 || (profile.staffViews ?? []).length > 0;
}
